import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, MessageSquare, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";
import { api, formatApiErrorDetail, getUTM } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please provide consent so we can respond to you.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", {
        ...form,
        consent,
        source_page: "/contact",
        utm: getUTM(),
      });
      toast.success(data.message || "Thanks — we'll be in touch!");
      track("contact_submit");
      setForm({ name: "", email: "", company: "", message: "" });
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <>
      <Seo
        title="Contact"
        description="Get in touch with the StUpEco Workspace team. We'd love to hear how you run your business."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Let's talk about your business."
        subtitle="Questions, partnerships, or just want to say hi? Send us a note and we'll get back to you."
      />
      <section className="border-b border-border py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal className="flex flex-col gap-6">
            {[
              { icon: Mail, title: "Email us", desc: "contact@stupeco.com" },
              { icon: MessageSquare, title: "Product questions", desc: "Ask anything about Workspace and the roadmap." },
              { icon: MapPin, title: "Remote-first", desc: "Building for founders, everywhere." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-7 sm:p-9" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Name</label>
                  <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name-input" placeholder="Jane Founder" />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium">Email</label>
                  <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email-input" placeholder="jane@company.com" />
                </div>
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium">Company <span className="text-muted-foreground">(optional)</span></label>
                <input value={form.company} onChange={update("company")} className={inputCls} data-testid="contact-company-input" placeholder="Acme Inc." />
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-medium">Message</label>
                <textarea required rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="contact-message-input" placeholder="How can we help?" />
              </div>
              <label className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="contact-consent-checkbox" className="mt-0.5 h-4 w-4 rounded border-border accent-brand-teal" />
                I consent to StUpEco Workspace storing my details to respond to this enquiry.
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="group mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full brand-gradient-bg px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"}
                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
